import type { Block } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import { type MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import {
  ShutterConfigurationType,
  ShutterType,
  ShutterVerticalAlignment,
} from "building-types/settings/settingsTypesEnum";
import { WindowPanelType } from "building-types/settings/windowTypesEnum";
import type { ShutterGap } from "building-types/windows/shuttersTypes";
import { ShutterStreetOrientationPreference } from "building-types/windows/shuttersTypes";
import { type WindowHomeSettings } from "building-types/windows/windowMergeTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNullOrUndefined, ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import deepequal from "common/utils/deepequal";
import objectFind from "common/utils/objectFind";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { intervalsOverlap } from "geom/aabb2";
import { DIST_EPSILON } from "geom/core";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeLength, planarGraphHalfEdgeNormal } from "geom/planarGraph";
import type { Vec2 } from "geom/vec2";
import { vec2Copy, vec2DistSquared } from "geom/vec2";
import { openingHalfEdgeParam } from "../elements/openings/openingUtils";
import { getWindowHeight } from "../elements/window/windowHeightTools";
import type { WindowShuttersPlacement } from "../elements/window/windowUtils";
import { windowShuttersPlacementNew } from "../elements/window/windowUtils";
import { isCombinedGlobalInstanceMirror } from "./generateOpeningsTools";

export function isWainscotingEnabledOnWindow(
  window: GeneratedWindowPlacement,
  walls: Building["walls"],
  wainscoting: Building["wainscoting"],
): boolean {
  if (!window.frontRoom) {
    return false;
  }

  const levelId = window.frontRoom.level.id;

  const levelWainscoting = objectFind(wainscoting, (x) => x.levelId === levelId);
  if (!levelWainscoting) {
    return false;
  }

  const wallId = walls[levelId].halfEdgeToWall[window.halfEdgeIndex];

  return levelWainscoting.faceInfo[wallId] !== undefined;
}

interface GetWindowShuttersPlacementsParams {
  readonly blocks: Table<Block>;
  readonly planarGraphs: Building["planarGraphs"];
  readonly planarGraphPerimeters: Building["planarGraphPerimeters"];
  readonly windows: Table<GeneratedWindowPlacement>;
  readonly windowHomeSettings: WindowHomeSettings;
  readonly mergeSettingsForOpeningParams: MergeSettingsForEntityParams;
  readonly mirror: boolean;
}

export function getWindowShuttersPlacements({
  blocks,
  planarGraphs,
  planarGraphPerimeters,
  windows,
  windowHomeSettings,
  mergeSettingsForOpeningParams,
  mirror,
}: GetWindowShuttersPlacementsParams): { [windowId: string]: WindowShuttersPlacement } {
  // Split perimeter into contiguous strips
  const levelPerimeterStrips: RWTable<number[][]> = {}; // level, half edges for perimeter pieces
  for (const levelId in planarGraphPerimeters) {
    levelPerimeterStrips[levelId] = splitPerimeterByNormals(
      planarGraphs[levelId].planarGraph,
      planarGraphPerimeters[levelId].coveredPerimeters.faceHalfEdges,
    ).map((v) => v.map((val) => planarGraphAdjacentHalfEdge(val)));
  }

  // Create a map of level -> half edge -> window ids
  const halfEdgeToWindowIds: RWTable<{ [halfEdge: number]: string[] }> = {};

  for (const levelId in planarGraphPerimeters) {
    halfEdgeToWindowIds[levelId] = {};
  }

  for (const windowId in windows) {
    const window = windows[windowId];
    if (!window.frontRoom) {
      continue;
    }

    const levelId = window.frontRoom.level.id;
    const currIds = halfEdgeToWindowIds[levelId][window.halfEdgeIndex];
    halfEdgeToWindowIds[levelId][window.halfEdgeIndex] = currIds ? currIds.concat([windowId]) : [windowId];
  }

  // Create figure out where in space (just x) each half edge is
  // Create a map of window id to strip index, so each window know what strip to search for neighbors
  const levelPerimeterStripLength: RWTable<number[]> = {};
  const windowIdToStripIndex: { [windowId: string]: number }[] = [];
  // Map of strip index to windowPositions
  const shutterGaps: { [stripIndex: number]: ShutterGap[] }[] = [];
  for (const levelId in planarGraphPerimeters) {
    const planarGraph = planarGraphs[levelId].planarGraph;
    const levelPerimeterStrip = levelPerimeterStrips[levelId];
    levelPerimeterStripLength[levelId] = [];
    shutterGaps[levelId] = {};
    windowIdToStripIndex[levelId] = {};
    for (let j = 0, jc = levelPerimeterStrip.length; j < jc; j++) {
      const strip = levelPerimeterStrip[j];
      let runningTotal = 0;
      let runningSubTotal = 0;
      let prevHalfEdgeRoomId: number | undefined = undefined;
      const stripShutterGaps: { [windowId: string]: ShutterGap } = {};
      for (let k = strip.length - 1; k >= 0; k--) {
        const halfEdge = levelPerimeterStrips[levelId][j][k];
        const halfEdgeLength = planarGraphHalfEdgeLength(planarGraph, halfEdge);

        const currHalfEdgeRoomId = planarGraphs[levelId].halfEdgeIndexToPolygonIndex(halfEdge);

        if (prevHalfEdgeRoomId === undefined || currHalfEdgeRoomId === prevHalfEdgeRoomId) {
          runningSubTotal += halfEdgeLength;
        } else {
          runningTotal += runningSubTotal;
          runningSubTotal = halfEdgeLength;
        }

        let windowIds = halfEdgeToWindowIds[levelId][halfEdge];
        const oppWindowIds = halfEdgeToWindowIds[levelId][planarGraphAdjacentHalfEdge(halfEdge)];
        if (oppWindowIds) {
          windowIds = windowIds ? [...windowIds, ...oppWindowIds] : oppWindowIds;
        }
        if (windowIds) {
          for (const windowId of windowIds) {
            windowIdToStripIndex[levelId][windowId] = j;
            const window = windows[windowId];
            if (!window.opening) {
              continue;
            }

            const { width } = window.descriptor;
            const height = getWindowHeight(window.descriptor);
            const { verticalOffset, headHeight = height } = window;

            const windowBottom = verticalOffset + headHeight - height;

            const halfEdgeParam = openingHalfEdgeParam(window, halfEdgeLength);

            const start = halfEdgeParam + runningTotal - width * 0.5;
            stripShutterGaps[windowId] = shutterGapNew(windowId, start, width, windowBottom, height);
          }
        }
        prevHalfEdgeRoomId = currHalfEdgeRoomId;
      }
      runningTotal += runningSubTotal;
      levelPerimeterStripLength[levelId][j] = runningTotal;
      shutterGaps[levelId][j] = stripShutterGaps;
    }
  }

  const { entitySettings, activeSettingsSets, settings, optionChoices } = mergeSettingsForOpeningParams;

  function tryAddShutter(
    window: GeneratedWindowPlacement,
    windowShuttersPlacement: WindowShuttersPlacement,
    stripIndex: number,
    stripShutterGaps: ShutterGap[],
    currShutterGap: ShutterGap,
    addRight: boolean,
  ): boolean {
    const { size: shutterWidth, bottom, height: shutterHeight, settings: shutterSettings } = windowShuttersPlacement;
    const top = bottom + shutterHeight;

    assertIsNotNullOrUndefined(shutterSettings);
    if (shutterSettings.shutterType === ShutterType.DISABLED) {
      return false;
    }

    const { horizontalOffset = 0, forceShutters = false } = shutterSettings;
    const panelType = window.materials?.panelType;

    let newExtendedStart = addRight
      ? currShutterGap.extendedStart
      : currShutterGap.extendedStart - shutterWidth - horizontalOffset;
    let newExtendedWidth = currShutterGap.extendedWidth + shutterWidth + horizontalOffset;

    if (panelType === WindowPanelType.FAUX_SHUTTER) {
      newExtendedStart = currShutterGap.extendedStart;
      newExtendedWidth = currShutterGap.width;
    }

    const center = newExtendedStart + newExtendedWidth * 0.5;
    const refStart = panelType === WindowPanelType.FAUX_SHUTTER ? center : newExtendedStart;
    const refEnd = panelType === WindowPanelType.FAUX_SHUTTER ? center : newExtendedStart + newExtendedWidth;

    if (
      !forceShutters &&
      (refStart < 0 || refEnd > levelPerimeterStripLength[ensureDefined(window.frontRoom).level.id][stripIndex])
    ) {
      // Won't fit on the border of the edge
      return false;
    }

    for (const windowPosId of Object.keys(stripShutterGaps)) {
      const shutterGap = stripShutterGaps[windowPosId];
      if (shutterGap.windowId === window.id) {
        continue;
      }

      // if we have an overlap...
      if (
        intervalsOverlap(
          newExtendedStart,
          newExtendedStart + newExtendedWidth,
          shutterGap.extendedStart,
          shutterGap.extendedStart + shutterGap.extendedWidth,
        )
      ) {
        // if we have aligned source (not extended) gaps...
        if (
          Math.abs(currShutterGap.start - shutterGap.start) < DIST_EPSILON &&
          Math.abs(currShutterGap.width - shutterGap.width) < DIST_EPSILON
        ) {
          // check for the already inserted shutter and update the vertical dimensions
          const insertedShutter = windowShutters[shutterGap.windowId];
          if (insertedShutter) {
            // check for same shutter parameters
            if (deepequal(insertedShutter.settings, shutterSettings)) {
              const existingTop = insertedShutter.bottom + insertedShutter.height;

              insertedShutter.bottom = Math.min(insertedShutter.bottom, bottom);
              insertedShutter.height = Math.max(existingTop, top) - insertedShutter.bottom;

              return false;
            }
          }
        }
        // they are not horizontally aligned, but we have vertical overlap...
        else if (
          intervalsOverlap(
            bottom,
            bottom + shutterHeight,
            shutterGap.extendedBottom,
            shutterGap.extendedBottom + shutterGap.extendedHeight,
          ) &&
          !forceShutters
        ) {
          return false;
        }
      }
    }

    return true;
  }

  const windowShutters: { [windowId: string]: WindowShuttersPlacement } = {};

  for (const windowId in windows) {
    const window = windows[windowId];
    const panelType = window.materials?.panelType;
    const windowWidth = window.descriptor.width / 2;

    const { shutters } = getMergedWindowSettings({
      homeSettings: windowHomeSettings,
      window,
      descriptor: window.descriptor,
      entitySettings,
      activeSettingsSets,
      settings,
      optionChoices,
    });
    const { frontRoom } = window;

    if (panelType === WindowPanelType.FAUX_SHUTTER && !frontRoom) {
      windowShutters[windowId] = windowShuttersPlacementNew(
        windowWidth,
        window.verticalOffset,
        window.descriptor.height,
        shutters,
        true,
        true,
      );
      continue;
    }

    if (!window.opening || !frontRoom) {
      continue;
    }

    const suppressShutters = blocks[frontRoom.parentId]?.suppressShutters;
    if (suppressShutters) {
      continue;
    }

    const levelId = frontRoom.level.id;

    const stripIndex = windowIdToStripIndex[levelId][windowId];
    const stripWindowPos = shutterGaps[levelId][stripIndex];

    if (!stripWindowPos || stripWindowPos.length === 0) {
      continue;
    }
    const windowStartWidth = stripWindowPos[windowId];

    // create faux shutter panels even when shutters are disabled
    if (panelType === WindowPanelType.FAUX_SHUTTER) {
      windowShutters[windowId] = windowShuttersPlacementNew(
        windowWidth,
        windowStartWidth.bottom,
        windowStartWidth.height,
        shutters,
        true,
        true,
      );

      continue;
    }

    const oppHalfEdge = planarGraphAdjacentHalfEdge(window.halfEdgeIndex);
    const streetOrientation = planarGraphPerimeters[levelId].coveredPerimeterStreetOrientations[oppHalfEdge];

    // setting the shutter type to disabled on a per-window or descriptor level disables shutters on that window
    const shuttersDisabledOnWindow =
      window.shutters?.shutterType && window.shutters.shutterType === ShutterType.DISABLED;
    const shuttersDisabledOnWindowDesc =
      window.descriptor.shutters?.shutterType && window.descriptor.shutters.shutterType === ShutterType.DISABLED;

    if (shuttersDisabledOnWindow || shuttersDisabledOnWindowDesc) {
      continue;
    }

    const shuttersEnabledOnWindow =
      window.shutters?.shutterType && (window.shutters.shutterType as any) !== ShutterType.DISABLED;
    const shuttersEnabledOnWindowDesc =
      window.descriptor.shutters?.shutterType &&
      (window.descriptor.shutters.shutterType as any) !== ShutterType.DISABLED;
    assertIsNotNullOrUndefined(shutters);
    const shuttersEnabledOnSettings = shutters.shutterType !== ShutterType.DISABLED;

    if (!shuttersEnabledOnWindow && !shuttersEnabledOnWindowDesc && !shuttersEnabledOnSettings) {
      continue;
    }

    // if shutters are enabled on a settings level, but not on a window/descriptor level, we need to verify the window
    // street orientation matches the orientation filter in the settings
    if (!shuttersEnabledOnWindow && !shuttersEnabledOnWindowDesc && shuttersEnabledOnSettings) {
      switch (shutters.orientation ?? ShutterStreetOrientationPreference.FRONT) {
        case ShutterStreetOrientationPreference.FRONT: {
          if (streetOrientation !== StreetOrientation.FRONT) {
            continue;
          }
          break;
        }
        case ShutterStreetOrientationPreference.NOT_BACK: {
          if (streetOrientation === StreetOrientation.BACK) {
            continue;
          }
          break;
        }
        case ShutterStreetOrientationPreference.ALL: {
          break;
        }
      }
    }

    if (shutters.shutterType === ShutterType.DISABLED) {
      continue;
    }

    const {
      shutterWidthOverride,
      shutterHeightOverride,
      isLegacyWidthOverride,
      verticalAlignment = ShutterVerticalAlignment.CENTER,
      verticalOffset = 0,
      horizontalOffset = 0,
    } = shutters;

    let shutterConfiguration = shutters.shutterConfiguration ?? ShutterConfigurationType.BOTH;

    const mirrorForShutters = isCombinedGlobalInstanceMirror(mirror, window.frontRoom ?? window.backRoom);
    if (mirrorForShutters && shutterConfiguration !== ShutterConfigurationType.BOTH) {
      shutterConfiguration =
        shutterConfiguration === ShutterConfigurationType.LEFT
          ? ShutterConfigurationType.RIGHT
          : ShutterConfigurationType.LEFT;
    }

    // make shutter placement
    const shutterDefaultWidth = isLegacyWidthOverride ? 0 : window.descriptor.width / 2;
    const shutterWidth = shutterWidthOverride ?? shutterDefaultWidth;
    const shutterHeight = shutterHeightOverride ?? windowStartWidth.height;

    if (shutterWidth < DIST_EPSILON || shutterHeight < DIST_EPSILON) {
      continue;
    }

    const heightDelta = shutterHeight - windowStartWidth.height;
    let bottom = windowStartWidth.bottom + verticalOffset;

    switch (verticalAlignment) {
      case ShutterVerticalAlignment.CENTER:
        bottom -= heightDelta / 2;
        break;
      case ShutterVerticalAlignment.BOTTOM:
        // already aligned to bottom
        break;
      case ShutterVerticalAlignment.TOP:
        bottom -= heightDelta;
        break;
      default:
        assertUnreachable(verticalAlignment);
    }

    const windowShuttersPlacement = windowShuttersPlacementNew(
      shutterWidth,
      bottom,
      shutterHeight,
      shutters,
      true,
      true,
    );

    // Try to add a half width shutter on both sides.
    windowShuttersPlacement.addRight =
      (shutterConfiguration === ShutterConfigurationType.BOTH ||
        shutterConfiguration === ShutterConfigurationType.RIGHT) &&
      tryAddShutter(window, windowShuttersPlacement, stripIndex, stripWindowPos, windowStartWidth, true);

    windowShuttersPlacement.addLeft =
      (shutterConfiguration === ShutterConfigurationType.BOTH ||
        shutterConfiguration === ShutterConfigurationType.LEFT) &&
      tryAddShutter(window, windowShuttersPlacement, stripIndex, stripWindowPos, windowStartWidth, false);

    const { addRight, addLeft } = windowShuttersPlacement;

    // no shutters to add
    if (!addRight && !addLeft) {
      continue;
    }

    // skip adding a single shutter when type is BOTH
    if (shutterConfiguration === ShutterConfigurationType.BOTH && (!addRight || !addLeft)) {
      continue;
    }

    windowShutters[windowId] = windowShuttersPlacement;
    // update extended height for placed shutter
    windowStartWidth.extendedBottom = bottom;
    windowStartWidth.extendedHeight = shutterHeight;

    // update extended width for placed shutter
    const totalShutterWidth = windowShuttersPlacement.size + horizontalOffset;

    if (addRight) {
      windowStartWidth.extendedWidth += totalShutterWidth;
    }
    if (addLeft) {
      windowStartWidth.extendedStart -= totalShutterWidth;
      windowStartWidth.extendedWidth += totalShutterWidth;
    }
  }

  return windowShutters;
}

function shutterGapNew(
  windowId: string,
  start: number,
  width: number,
  bottom: number,
  height: number,
  extendedStart = start,
  extendedWidth = width,
  extendedBottom = bottom,
  extendedHeight = height,
): ShutterGap {
  return {
    windowId,
    start,
    width,
    bottom,
    height,
    extendedStart,
    extendedWidth,
    extendedBottom,
    extendedHeight,
  };
}

function splitPerimeterByNormals(planarGraph: PlanarGraph, faceEdgeLoop: number[][]): number[][] {
  let strips: number[][] = [];
  for (let f = 0, c = faceEdgeLoop.length; f < c; f++) {
    strips = strips.concat(splitHalfEdgeStripByNormals(planarGraph, faceEdgeLoop[f]));
  }
  return strips;
}

function splitHalfEdgeStripByNormals(planarGraph: PlanarGraph, halfEdgeLoop: number[]): number[][] {
  const strips: number[][] = [[]];
  let prevNormal: Vec2 | undefined;
  for (let i = 0, c = halfEdgeLoop.length; i < c; i++) {
    const halfEdge = halfEdgeLoop[i];
    const edgeNormal = planarGraphHalfEdgeNormal(planarGraph, halfEdge);
    if (!prevNormal || vec2DistSquared(edgeNormal[0], edgeNormal[1], prevNormal[0], prevNormal[1]) < 0.01) {
      if (!prevNormal) {
        prevNormal = vec2Copy(edgeNormal);
      }
      strips[strips.length - 1].push(halfEdge);
    } else {
      prevNormal = vec2Copy(edgeNormal);
      strips.push([halfEdge]);
    }
  }

  if (strips.length >= 2) {
    const firstStrip = strips[0];
    const lastStrip = strips[strips.length - 1];

    const [firstNormalX, firstNormalY] = planarGraphHalfEdgeNormal(planarGraph, firstStrip[0]);
    const [lastNormalX, lastNormalY] = planarGraphHalfEdgeNormal(planarGraph, lastStrip[0]);

    if (vec2DistSquared(firstNormalX, firstNormalY, lastNormalX, lastNormalY) < 0.01) {
      strips[0] = lastStrip.concat(firstStrip);
      strips.pop();
    }
  }

  return strips;
}
